import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { SelectItem, LazyLoadEvent } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import { Subscription, Observable, of } from 'rxjs';
import {
  EventSearchType,
  ProgramTypesGUID,
  EventStatus,
  EventStatusNames,
  ProgramTypes,
  EventDocumentTypeIds
} from '../../../../models/_enum-collections';
import { CustomerPreviewInput } from '../../../../models/customer-validation-preview-input.model';
import { EventPreviewInput } from '../../../../models/event-preview-input.model';
import { EventRegistrationModel } from '../../../../models/event-registration.model';
import { EventSearchFilterRequest } from '../../../../models/event-search-filter-request.model';
import { PagedResult } from '../../../../models/page-result.model';
import { AppConstants } from '../../../common/app-constants';
import { CommonSharedModule } from '../../../common/shared.module';
import { LocalStorageService } from '../../../services/local-storage-service';
import { OsmosisEditService } from '../../../services/osmosis-edit.service';

import { EditEventComponent } from '../edit-event/edit-event.component';
import { EventPreviewComponent } from '../../preview/event-preview/event-preview.component';
import { CustomerSurveyPreviewComponent } from '../../preview/customer-survey-preview/customer-survey-preview.component';
import { CustomerValidationPreviewComponent } from '../../preview/customer-validation-preview/customer-validation-preview.component';
import { EventDashboardService } from '../../../services/event-dashboard.service';
import { EventTypeService } from '../../../services/event-type.service';

@Component({
  selector: 'app-event-review-table',
  templateUrl: './event-review-table.component.html',
  styleUrls: ['./event-review-table.component.scss'],
  standalone: true,
  imports: [
    CommonSharedModule,
    EditEventComponent,
    EventPreviewComponent,
    CustomerSurveyPreviewComponent,
    CustomerValidationPreviewComponent
  ]
})
export class EventReviewTableComponent implements OnInit, OnChanges {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: LocalStorageService,
    private eventDashboardService: EventDashboardService,
    private osmService: OsmosisEditService,
    private eventTypeService: EventTypeService
  ) {}

  excelLoading = false;
  @Input() loading!: boolean;
  scrollableCols = new Array<any>();
  loadingIcon = 'fa-circle-o-notch';
  dataViewRecsPerPage!: number;
  pageLinksPerPage!: number;
  tblPageNumber = 1;
  totalRecords!: number;
  loadEditComponent!: boolean;
  editNav = false;
  eventIdPassToChild!: number;
  display!: boolean;
  showFullScreen!: boolean;
  canShowEventPreviewModel!: boolean;
  isMrppCustomerValidationReviewScreen = false;
  @Input() eventSearchType!: EventSearchType;
  eventEditInput!: EventRegistrationModel;
  eventsDisplayed!: EventRegistrationModel[];
  allEventsDisplayed!: EventRegistrationModel[];
  eventSearchSubscription!: Subscription;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  delayedLoadSearchContent!: Function;
  eventSearchFilterRequest!: EventSearchFilterRequest;
  eventStatusDropdownItems: SelectItem[] = [];
  l1InternalStatusDropdownItems: SelectItem[] = [];
  l2InternalStatusDropdownItems: SelectItem[] = [];
  eventTypeDropdownItems: SelectItem[] = [];
  @ViewChild('dt', { static: false }) dt!: Table;
  @ViewChild('editNavChild', { static: false })
  editNavChild!: EditEventComponent;
  @ViewChildren('tak') components!: QueryList<MultiSelect>;
  customerValidationPreviewInput!: CustomerPreviewInput;
  customerSurveyPreviewInput!: CustomerPreviewInput;
  eventPreviewInput!: EventPreviewInput;
  canShowCustomerPreviewModel!: boolean;
  canShowCustomerValidationPreviewModel!: boolean;
  canShowCustomerSurveyPreviewModel!: boolean;
  customerValidationReviewEventSearchType: EventSearchType =
    EventSearchType.CustomerValidationReview;
  invoiceDocReviewEventSearchType: EventSearchType =
    EventSearchType.InvoiceDocReview;
  rollCallReviewEventSearchType: EventSearchType =
    EventSearchType.RollCallReview;
  customerSurveyReviewEventSearchType: EventSearchType =
    EventSearchType.CustomerSurveyReview;
  skuPurchaseTrialActivationReviewEventSearchType: EventSearchType =
    EventSearchType.SKUPurchaseTrialActivationReview;
  daPOEReviewEventSearchType: EventSearchType =
    EventSearchType.DealAssociationPOETemplate;
  SOWReviewEventSearchType: EventSearchType = EventSearchType.SOWDocument;

  showTargetProduct = false;
  programGuid!: string;

  ngOnInit() {
    this.programGuid = this.eventDashboardService.getProgramTypeGuidFromUrl();
    this.loadEventStatusDropdownItems();
    this.getEventTypeList();
    this.getL1InternalStatusList();
    this.getL2InternalStatusList();
    this.isMrppCustomerValidationReviewScreen =
      this.eventSearchType == EventSearchType.CustomerValidationReview &&
      this.programGuid == ProgramTypesGUID.MRPP;
    this.delayedLoadSearchContent = _.debounce(this.loadEvents, 1000);

    this.dataViewRecsPerPage = AppConstants.dataListRecsPerPage;
    this.pageLinksPerPage = AppConstants.dataListPagerLinks;

    this.eventSearchFilterRequest = {
      pageSize: this.dataViewRecsPerPage,
      page: this.tblPageNumber,
      programTypeGuid: ProgramTypesGUID.Events
    };

    this.loadEvents({ first: 0 });

    this.scrollableCols = [
      { field: 'eventKey', header: 'Event Id' },
      { field: 'eventName', header: 'Workshop Type' },
      { field: 'partnerOrgName', header: 'Partner Org' },
      { field: 'fiscalYear', header: 'Fiscal Year' },
      { field: 'partnerMpnid', header: 'Partner MPN ID' },
      { field: 'l1InternalStatus', header: 'L1 Internal Status' },
      { field: 'l2InternalStatus', header: 'L2 Internal Status' },
      { field: 'poeDocUploadDate', header: 'Uploaded Date' },
      { field: 'submittedDate', header: 'Submitted Date' }
    ];
    this.localStorage.setFullscreenMode(true);
    this.showFullScreen = this.localStorage.getFullscreenMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['eventSearchType'] && !changes['eventSearchType'].firstChange) {
      this.loadEvents({});
    }
  }

  loadEventStatusDropdownItems(): void {
    const statusKeys = Object.keys(EventStatus).filter(
      x => !isNaN(parseInt(x, 0))
    );

    for (const key of statusKeys) {
      const itm: SelectItem = {
        label: EventStatusNames[EventStatus[parseInt(key, 0)]],
        value: EventStatusNames[EventStatus[parseInt(key, 0)]]
      };
      this.eventStatusDropdownItems.push(itm);
    }
  }

  getL1InternalStatusList() {
    this.osmService.getL1InternalStatusDropDown().subscribe(PTList => {
      for (const item of PTList) {
        const fl1InternalStatusDrpdwn: SelectItem = {
          label: item.l1internalStatus,
          value: item.l1internalStatus
        };
        this.l1InternalStatusDropdownItems.push(fl1InternalStatusDrpdwn);
      }
    });
  }

  getL2InternalStatusList() {
    this.osmService.getL2InternalStatusDropDown().subscribe(PTList => {
      for (const item of PTList) {
        const fl1InternalStatusDrpdwn: SelectItem = {
          label: item.l2internalStatus,
          value: item.l2internalStatus
        };
        this.l2InternalStatusDropdownItems.push(fl1InternalStatusDrpdwn);
      }
    });
  }

  loadEvents(event: LazyLoadEvent, refreshAfterPreview = false): void {
    if (!refreshAfterPreview) {
      if (event) {
        this.pageChange(event);
      } else {
        this.pageChange({ first: 0 });
      }
    }

    this.loading = true;
    if (this.eventSearchSubscription) {
      this.eventSearchSubscription.unsubscribe();
    }
    const programType = this.eventDashboardService.getProgramTypeGuidFromUrl();
    if (programType == ProgramTypesGUID.MRPP) {
      if (this.eventSearchType == EventSearchType.CustomerValidationReview) {
        this.eventSearchFilterRequest.programTypeId = ProgramTypes.MRPP;
        this.eventSearchFilterRequest.programTypeGuid = ProgramTypesGUID.MRPP;
        this.eventSearchSubscription = this.eventDashboardService
          .getAwaitingCustomerValidationEvents(this.eventSearchFilterRequest)
          .subscribe(data => {
            this.eventsDisplayed = data.results;
            this.totalRecords = data.rowCount;
            this.loading = false;
          });
      } else {
        this.eventSearchSubscription = this.eventDashboardService
          .getPOEDocReviewEngagements(this.eventSearchFilterRequest)
          .subscribe(data => {
            this.eventsDisplayed = data.results;
            this.totalRecords = data.rowCount;
            this.loading = false;
          });
      }
    } else {
      switch (this.eventSearchType) {
        case EventSearchType.AllEvents:
          this.eventSearchSubscription = this.eventDashboardService
            .getAllEvents(this.eventSearchFilterRequest)
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
          break;
        case EventSearchType.CustomerValidationReview:
          this.eventSearchSubscription = this.eventDashboardService
            .getAwaitingCustomerValidationEvents(this.eventSearchFilterRequest)
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
          break;
        case EventSearchType.InvoiceDocReview:
          this.eventSearchSubscription = this.eventDashboardService
            .getAwaitingInvoiceDocReviewEvents(this.eventSearchFilterRequest)
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
          break;
        case EventSearchType.CustomerSurveyReview:
          this.eventSearchSubscription = this.eventDashboardService
            .getAwaitingCustomerSurveyReviewEvents(
              this.eventSearchFilterRequest
            )
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
          break;
        case EventSearchType.SKUPurchaseTrialActivationReview:
          this.eventSearchSubscription = this.eventDashboardService
            .getAwaitingSKUPurchaseReviewEvents(this.eventSearchFilterRequest)
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
          break;
        case EventSearchType.DealAssociationPOETemplate:
          this.eventSearchSubscription = this.eventDashboardService
            .getAwaitingDAPoeReviewEvents(this.eventSearchFilterRequest)
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
          break;
        case EventSearchType.SOWDocument:
          this.eventSearchFilterRequest.eventDocumentTypeId =
            EventDocumentTypeIds.SOW;
          this.eventSearchSubscription = this.eventDashboardService
            .getPOEDocReviewEngagements(this.eventSearchFilterRequest)
            .subscribe(data => {
              this.eventsDisplayed = data.results;
              this.totalRecords = data.rowCount;
              this.loading = false;
            });
      }
    }
  }

  export() {
    this.router.navigate(['download-csv'], { relativeTo: this.route });
  }

  pageChange(e) {
    const calc = e.first / this.dataViewRecsPerPage + 1;

    this.tblPageNumber = calc || 1;
    this.eventSearchFilterRequest.page = this.tblPageNumber;
  }

  onSearchInput(event, dt) {
    if (!event.target.value || event.target.value.trim() !== '') {
      dt.filterGlobal(event.target.value, 'contains');
    }
  }

  applyFilter(event) {
    this.totalRecords = event.filteredValue.length;
    this.pageChange({ first: 0 });
  }

  searchOrFilterEngagements() {
    this.eventSearchFilterRequest.page = 1;
    if (this.dt) {
      this.dt.first = this.eventSearchFilterRequest.page;
    }

    if (
      this.eventSearchFilterRequest.eventKey != undefined &&
      this.eventSearchFilterRequest.eventKey.toString() === ''
    ) {
      this.eventSearchFilterRequest.eventKey = undefined;
    }

    if (
      this.eventSearchFilterRequest.partnerOrgName != undefined &&
      this.eventSearchFilterRequest.partnerOrgName.toString() === ''
    ) {
      this.eventSearchFilterRequest.partnerOrgName = undefined;
    }

    if (
      this.eventSearchFilterRequest.partnerMpnid != undefined &&
      this.eventSearchFilterRequest.partnerMpnid.toString() === ''
    ) {
      this.eventSearchFilterRequest.partnerMpnid = undefined;
    }

    if (
      this.eventSearchFilterRequest.fiscalYear != undefined &&
      this.eventSearchFilterRequest.fiscalYear.toString() === ''
    ) {
      this.eventSearchFilterRequest.fiscalYear = undefined;
    }

    this.delayedLoadSearchContent();
  }

  get currentPageInfo() {
    if (this.totalRecords === 0) {
      return 'No Record Found';
    } else if (this.totalRecords > 0 && this.tblPageNumber === 1) {
      return `Showing 1 - ${this.eventsDisplayed.length} of ${this.totalRecords} Records`;
    } else if (this.totalRecords > 0 && this.tblPageNumber > 1) {
      const rangeStart =
        (this.tblPageNumber - 1) * this.dataViewRecsPerPage + 1;
      let rangeEnd = 0;

      if (this.tblPageNumber * this.dataViewRecsPerPage >= this.totalRecords) {
        rangeEnd = this.totalRecords;
      } else if (
        this.tblPageNumber * this.dataViewRecsPerPage <
        this.totalRecords
      ) {
        rangeEnd = this.tblPageNumber * this.dataViewRecsPerPage;
      }

      return `Showing ${rangeStart} - ${rangeEnd} of ${this.totalRecords} Records`;
    }
    return '';
  }

  reset() {
    this.components['_results'].forEach(ds => {
      ds.value = null;
    });

    if (this.dt) {
      this.dt.filter('', 'taskOwner', 'in');
      this.dt.filter('', 'l1InternalStatus', 'in');
      this.dt.filter('', 'l2InternalStatus', 'in');
      this.dt.filter('', 'engagementType', 'in');
      this.dt.reset();
    }

    this.eventSearchFilterRequest = {
      pageSize: this.dataViewRecsPerPage,
      page: this.tblPageNumber
    };

    this.display = false;
  }

  editRequestDetails(id: number) {
    this.loadEditComponent = true;
    this.editNav = true;
    this.eventIdPassToChild = id;
    this.eventEditInput = this.eventsDisplayed.filter(
      x => x.eventRegistrationId === id
    )[0];
  }

  previewFile(eventRegistrationId) {
    this.GetAllEventIds().subscribe(data => {
      this.allEventsDisplayed = data.results;
      this.loading = false;

      this.eventPreviewInput = {
        eventId: eventRegistrationId,
        previewFileIndex: 0,
        eventIdList: this.allEventsDisplayed.map(
          event => event.eventRegistrationId
        ),
        isPoeInvoicePreview: false,
        isRollCallPreview: false,
        isSKUPurchaseTrialPreview: false,
        isDAPOETemplate: false,
        isSOWPreview: false,
        eventCustomers: []
      } as any;

      if (this.eventSearchType === EventSearchType.InvoiceDocReview) {
        this.eventPreviewInput.isPoeInvoicePreview = true;
        this.eventPreviewInput.isRollCallPreview = false;
        this.eventPreviewInput.isSKUPurchaseTrialPreview = false;
        this.eventPreviewInput.isDAPOETemplate = false;
        this.eventPreviewInput.isSOWPreview = false;
      } else if (this.eventSearchType === EventSearchType.RollCallReview) {
        this.eventPreviewInput.isPoeInvoicePreview = false;
        this.eventPreviewInput.isSKUPurchaseTrialPreview = false;
        this.eventPreviewInput.isRollCallPreview = true;
        this.eventPreviewInput.isDAPOETemplate = false;
        this.eventPreviewInput.isSOWPreview = false;
      } else if (
        this.eventSearchType == EventSearchType.SKUPurchaseTrialActivationReview
      ) {
        this.eventPreviewInput.isPoeInvoicePreview = false;
        this.eventPreviewInput.isRollCallPreview = false;
        this.eventPreviewInput.isDAPOETemplate = false;
        this.eventPreviewInput.isSKUPurchaseTrialPreview = true;
        this.eventPreviewInput.isSOWPreview = false;
        this.eventPreviewInput.eventCustomers = data.results.map(
          item =>
            ({
              eventId: item.eventRegistrationId,
              eventCustomers: item.eventCustomers
            }) as any
        );
      } else if (
        this.eventSearchType == EventSearchType.DealAssociationPOETemplate
      ) {
        this.eventPreviewInput.isPoeInvoicePreview = false;
        this.eventPreviewInput.isRollCallPreview = false;
        this.eventPreviewInput.isSKUPurchaseTrialPreview = false;
        this.eventPreviewInput.isDAPOETemplate = true;
        this.eventPreviewInput.isSOWPreview = false;
        this.eventPreviewInput.eventCustomers = data.results.map(
          item =>
            ({
              eventId: item.eventRegistrationId,
              eventCustomers: item.eventCustomers
            }) as any
        );
      } else if (this.eventSearchType == EventSearchType.SOWDocument) {
        this.eventPreviewInput.isPoeInvoicePreview = false;
        this.eventPreviewInput.isRollCallPreview = false;
        this.eventPreviewInput.isSKUPurchaseTrialPreview = false;
        this.eventPreviewInput.isDAPOETemplate = false;
        this.eventPreviewInput.isSOWPreview = true;
        this.eventPreviewInput.eventCustomers = data.results.map(
          item =>
            ({
              eventId: item.eventRegistrationId,
              eventCustomers: item.eventCustomers
            }) as any
        );
      }

      this.canShowEventPreviewModel = true;
    });
  }

  closePreview() {
    this.canShowEventPreviewModel = false;
  }

  onShowFullScreenChanged(showFullscreenValue: boolean) {
    this.showFullScreen = showFullscreenValue;
    this.localStorage.setFullscreenMode(this.showFullScreen);
  }

  refresh() {
    this.loadEvents({}, true);
  }

  sort(columnKey: string) {
    const column = this.scrollableCols.find(x => x.field === columnKey);

    column.descending =
      column.descending === undefined ? true : !column.descending;

    const columnIndex = this.scrollableCols.indexOf(column);

    this.scrollableCols[columnIndex] = column;

    this.eventSearchFilterRequest.sortField = column.field;
    this.eventSearchFilterRequest.sortOrderDescending = column.descending;
    this.loadEvents({});
  }

  GetAllEventIds(): Observable<PagedResult<EventRegistrationModel>> {
    this.loading = true;
    const filter: EventSearchFilterRequest = {
      pageSize: 100000,
      page: 1,
      programTypeGuid: this.eventSearchFilterRequest.programTypeGuid,
      programTypeId: this.eventSearchFilterRequest.programTypeId,
      sortOrderDescending: this.eventSearchFilterRequest.sortOrderDescending,
      sortField: this.eventSearchFilterRequest.sortField
    };

    switch (this.eventSearchType) {
      case EventSearchType.AllEvents:
        return this.eventDashboardService.getAllEvents(filter);
        break;
      case EventSearchType.CustomerValidationReview:
        return this.eventDashboardService.getAwaitingCustomerValidationEvents(
          filter
        );
        break;
      case EventSearchType.InvoiceDocReview:
        return this.eventDashboardService.getAwaitingInvoiceDocReviewEvents(
          filter
        );
        break;
      case EventSearchType.CustomerSurveyReview:
        return this.eventDashboardService.getAwaitingCustomerSurveyReviewEvents(
          filter
        );
        break;
      case EventSearchType.SKUPurchaseTrialActivationReview:
        return this.eventDashboardService.getAwaitingSKUPurchaseReviewEvents(
          filter
        );
        break;
      case EventSearchType.DealAssociationPOETemplate:
        return this.eventDashboardService.getAwaitingDAPoeReviewEvents(filter);
        break;
      case EventSearchType.SOWDocument:
        filter.eventDocumentTypeId = EventDocumentTypeIds.SOW;
        return this.eventDashboardService.getPOEDocReviewEngagements(filter);
        break;
    }
    return of();
  }

  showCustomerValidationPreview(eventRegistrationId) {
    this.GetAllEventIds().subscribe(data => {
      this.allEventsDisplayed = data.results;
      this.loading = false;
      this.canShowCustomerValidationPreviewModel = true;
      this.customerValidationPreviewInput = {
        eventId: eventRegistrationId,
        eventIdList: this.allEventsDisplayed.map(
          event => event.eventRegistrationId
        )
      };
    });
  }

  showCustomerSurveyPreview(eventRegistrationId) {
    this.GetAllEventIds().subscribe(data => {
      this.allEventsDisplayed = data.results;
      this.loading = false;
      this.canShowCustomerSurveyPreviewModel = true;
      this.customerSurveyPreviewInput = {
        eventId: eventRegistrationId,
        eventIdList: this.allEventsDisplayed.map(
          event => event.eventRegistrationId
        )
      };
    });
  }
  closeCustomerValidationPreview() {
    this.canShowCustomerValidationPreviewModel = false;
  }
  closeCustomerSurveyPreview() {
    this.canShowCustomerSurveyPreviewModel = false;
  }

  onCloseEditModal() {
    this.loadEditComponent = false;
    this.editNav = false;
    this.refresh();
    this.setEventStatus();
  }

  async setEventStatus() {
    const event = await this.loadEventInfo();
    if (event) {
      const eventIndex = this.eventsDisplayed.indexOf(
        this.eventsDisplayed.find(
          event =>
            event.eventRegistrationId ===
            this.eventEditInput.eventRegistrationId
        ) as EventRegistrationModel
      );
      this.eventsDisplayed[eventIndex] = event;
    }
  }

  async loadEventInfo() {
    return await this.eventDashboardService
      .getEventRegistration(this.eventEditInput.eventRegistrationId)
      .toPromise();
  }

  getUploadedDate(rawData: EventRegistrationModel): string | undefined {
    if (rawData.eventRegistrationDocuments != null) {
      switch (this.eventSearchType) {
        case EventSearchType.InvoiceDocReview: {
          return rawData.eventRegistrationDocuments.find(
            doc => doc.eventDocumentTypeId == EventDocumentTypeIds.POEInvoice
          )?.formattedUploadDate;
        }
        case EventSearchType.RollCallReview: {
          return rawData.eventRegistrationDocuments.find(
            doc => doc.eventDocumentTypeId == EventDocumentTypeIds.RollCall
          )?.formattedUploadDate;
        }
        case EventSearchType.SKUPurchaseTrialActivationReview: {
          return rawData.eventRegistrationDocuments.find(
            doc => doc.eventDocumentTypeId == EventDocumentTypeIds.POESKUTrial
          )?.formattedUploadDate;
        }
        case EventSearchType.DealAssociationPOETemplate: {
          return rawData.eventRegistrationDocuments.find(
            doc =>
              doc.eventDocumentTypeId ==
              EventDocumentTypeIds.DealAssociationTemplate
          )?.formattedUploadDate;
        }
        case EventSearchType.SOWDocument: {
          return rawData.eventRegistrationDocuments.find(
            doc => doc.eventDocumentTypeId == EventDocumentTypeIds.SOW
          )?.formattedUploadDate;
        }
        default:
          return undefined;
      }
    }
    return undefined;
  }

  getEventTypeList() {
    this.eventTypeService
      .getRegisteredEventTypes(ProgramTypesGUID.Events)
      .subscribe(PTList => {
        for (const item of PTList) {
          const eventTypeDrpdwn: SelectItem = {
            label: item,
            value: item
          };
          this.eventTypeDropdownItems.push(eventTypeDrpdwn);
        }
      });
  }
}
